import { TooltipWrapper } from 'components/TooltipWrapper'
import type { DocumentSearchMode } from 'features/knowledgeDocumentViewer/types'
import { Info } from 'lucide-react'
import React from 'react'
import styled from 'styled-components'

const ToggleWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
  color: ${(props) => props.theme.forecast.activeText};
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.25rem;
  user-select: none;
`

const ToggleText = styled.span`
  display: inline-flex;
  align-items: center;
  height: 1.25rem;
`

const SwitchLabel = styled.label`
  position: relative;
  display: inline-flex;
  align-items: center;
  height: 1.25rem;
  cursor: pointer;
  border-radius: 9999rem;
`

const ToggleInput = styled.input`
  position: absolute;
  opacity: 0;
  inset: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  cursor: pointer;
  z-index: 1;
`

const InfoIconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  height: 1.25rem;
  color: ${(props) => props.theme.forecast.secondaryText};
  flex-shrink: 0;
`

const InfoTooltipSlot = styled.span`
  display: inline-flex;
  align-items: center;
  height: 1.25rem;
`

const SwitchTrack = styled.span`
  position: relative;
  width: 2.5rem;
  height: 1.25rem;
  border-radius: 9999rem;
  background: ${(props) => props.theme.forecast.background4};
  border: 0.0625rem solid ${(props) => props.theme.forecast.border2};
  transition:
    background 0.16s ease,
    border-color 0.16s ease,
    box-shadow 0.16s ease;
  flex-shrink: 0;

  &::after {
    content: '';
    position: absolute;
    top: 0.1875rem;
    left: 0.1875rem;
    width: 1.25rem;
    height: 0.75rem;
    border-radius: 9999rem;
    background: #ffffff;
    box-shadow: 0 0.0625rem 0.125rem rgba(0, 0, 0, 0.24);
    transition: transform 0.16s ease;
  }

  ${ToggleInput}:checked + & {
    background: ${(props) =>
      props.theme.colors.success['400_base'] ?? '#40b74e'};
    border-color: ${(props) => props.theme.colors.green['500_base']};
  }

  ${ToggleInput}:checked + &::after {
    background: #ffffff;
    transform: translateX(0.75rem);
  }

  ${ToggleInput}:focus-visible + & {
    box-shadow: 0 0 0 0.125rem
      ${(props) => props.theme.colors.success['200'] ?? '#c1f7b7'};
  }

  ${SwitchLabel}:hover ${ToggleInput}:not(:checked) + & {
    background: ${(props) => props.theme.forecast.activeButton};
  }

  ${SwitchLabel}:hover ${ToggleInput}:checked + & {
    background: ${(props) =>
      props.theme.colors.success['450'] ??
      props.theme.colors.success['400_base'] ??
      '#3e8334'};
  }
`

export interface DocumentSearchModeToggleProps {
  searchMode: DocumentSearchMode
  onSearchModeChange: (mode: DocumentSearchMode) => void
}

const AI_SEARCH_TOOLTIP =
  'Search with AI to find relevant documents and narrow results with follow-up questions.'

export function DocumentSearchModeToggle({
  searchMode,
  onSearchModeChange,
}: DocumentSearchModeToggleProps): React.ReactElement {
  const isAgentic = searchMode === 'agentic'

  return (
    <ToggleWrapper>
      <ToggleText>AI Search</ToggleText>
      <InfoTooltipSlot>
        <TooltipWrapper label={AI_SEARCH_TOOLTIP} side='top' offset={8}>
          <InfoIconWrapper aria-label={AI_SEARCH_TOOLTIP}>
            <Info size='1rem' />
          </InfoIconWrapper>
        </TooltipWrapper>
      </InfoTooltipSlot>
      <SwitchLabel title='Toggle AI Search'>
        <ToggleInput
          type='checkbox'
          checked={isAgentic}
          onChange={(event) =>
            onSearchModeChange(event.target.checked ? 'agentic' : 'normal')
          }
          aria-label='Toggle AI Search'
        />
        <SwitchTrack aria-hidden='true' />
      </SwitchLabel>
    </ToggleWrapper>
  )
}
